/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/
import React, { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { PlayerCharts } from "./PlayerCharts";
import { Navbar } from "../Navigation/Navbar";
import { Space, DatePicker, Spin, Popover } from "antd";
import { Button, Tooltip, FloatButton, Tabs, Collapse, Switch, Col, Row, Checkbox, Popconfirm, Typography, Badge, Tag } from "antd";
import { TrainingAPI } from "../../api/TrainingAPI";
import { FileTwoTone } from "@ant-design/icons";
import dayjs from "dayjs";
import { Timeline } from "antd";
import { Select } from "antd";
import ForcePlate from "../ForcePlate/ForcePlate";
import TestingData from "../TestingData/TestingData";
import Nutrition from "../Nutrition/Nutrition";
import Cognition from "../Cognition/Cognition";
import HelmetImpact from "../HelmetImpact/HelmetImpact";
import { HelmetImpactPitch } from "../HelmetImpact/HelmetImpactPitch";
import "../../Styles/styles-TeamData.css";
import "../../Styles/styles-PostData.css";
import { getPrediction } from "../../Utils/chart_utils";
import { Subjective } from "../Subjective/Subjective";
import {
	custom_fp_metrics,
	fp_metrics,
} from "../../Utils/fp_metrics";
import { formattedPlayerName } from "../../Utils/name_utils";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
	changePlayerFPMetric,
	getPlayerDrillCostPerMin,
	getPlayerGPSData,
	setPlayerFilter,
	getPlayerMovementSignatureData
} from "../../store/actions/player.actions";
import { getLastDayTargetDate } from "../../store/actions/team.actions";
import { renderAsPDF } from "../../Utils/pdf_utils";
import { LoadingComponent } from "../Common/LoadingComponent";
import Profile from "../Profile/Profile";
import { HelpButton } from "../Common/HelpButton";
import {TfiLock} from "react-icons/tfi";
import { audioMessage as message } from "../Common/Notifications/audioMessage";
import { DataColorUtil } from "../../Utils/color_utils";
import {PlayerOptions} from "@components/Common/PlayerOptions";
import { PermissionedButton, useDataAccess, DataAccessGuard } from "@components/PermissionedComponents";
import { DATA_STREAM_PERMISSIONS } from "@utils/data_access";
import { NotesTab } from "@components/Notes/NotesTab";
import { useCanViewNotes } from "@hooks/useCanViewNotes";


const { Panel } = Collapse;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;
const { TabPane } = Tabs;

const PDF_EXPORT_TARGETS = {
	"0": { chartSelector: ".player-profile-chart" },
	"1": { chartSelector: ".example-table-graph" },
	"2": { chartSelector: ".wellness-stacked-chart, .rpe-chart, .subjective-chart" },
	"3": { chartSelector: ".fp-testing-chart" },
	"4": { chartSelector: ".fp-testing-chart", withTable: true },
	"7": { chartSelector: ".helmet-export", widgetSelector: ".helmet-export-tiles", withTable: true },
};

function PlayerData(props) {
  let location = useLocation();
  const dispatch = useDispatch();
  	const {
		games,
		players,
		hasWellness,
		forceplateStatDefaults,
		testingStatDefaults,
		all_metric_types: allMetricTypes,
		helmet_metrics: helmetMetrics,
		sessions: session_list,
		info: {
			display_name: teamName,
			hideDistanceZones,
			movement_sig_chart_mapping,
			coach_list,
			dataviz_lite: datavizLite,
			sport
		},
		playersStatus,
		playerProfileStatDefaults,
		presets,
		profilePresetName,
		color_strategies: colorStrategies
	} = useSelector(({ team }) => team);

    const {
		loading: { gps: loading },
		trainings,
		practicePlan,
		costPerMin,
		fpmsData,
		fpMetrics,
		filter: {
			showGameDays,
			showPredictions,
			showWeekly,
			showAllDays,
			startDate,
			endDate,
			player,
			session,
		},
	} = useSelector(({ player }) => player);

	const {
		currentUser: { email: currentUserEmail },
	} = useSelector(({ auth }) => auth);

	let trainingAPI = new TrainingAPI();

	const [exportPreview, setExportPreview] = React.useState(false);
	// The tabs follow this state, so the Profile tab can open the Notes tab. It starts on Profile, the first tab.
	const [activeTab, setActiveTab] = React.useState("0");
	const [helmetSearchCount, setHelmetSearchCount] = React.useState(0);

	const { canAccess, ready: permissionsReady } = useDataAccess();
	const canViewNotes = useCanViewNotes();

	// Tab "0" is the player profile, which draws on whichever streams are permitted.
	const TAB_ID_TO_STREAM = {
		"1": "gps",
		"2": "wellness",
		"3": "forceplate",
		"4": "testing",
		"5": "nutrition",
		"6": "cognition",
		"7": "helmet",
	};

	const activeStream = TAB_ID_TO_STREAM[activeTab];
	const activeStreamPermission = activeStream
		? DATA_STREAM_PERMISSIONS[activeStream].access
		: null;
	const [rendering, setRendering] = React.useState(false);
	const [selectedEmails, setSelectedEmails] = React.useState({});
	const [selectedReportDatatype, setSelectedReportDatatype] = React.useState([]);
	const [showTravel, setShowTravel] = React.useState(false);
	const [showRest, setShowRest] = React.useState(false);
	const [showACWR, setShowACWR] = React.useState(false);
	const [bulkGenReportCards, setBulkGenReportCards] = React.useState(false);
	const [dataColorUtil, setDataColorUtil] = React.useState(null);

	const toggleOnlyGameDays = (checked) => {
		dispatch(setPlayerFilter({ showGameDays: checked }));
	};

	const handleTogglePredictions = async (checked) => {
		if (!startDate || !endDate) {
			message.error("Please select start and end dates before toggling predictions.");
			return;
		}
		
		dispatch(setPlayerFilter({ showPredictions: checked }));
		if (checked) {
			const lastDayTargetDate = await getLastDayTargetDate();

			let endDate = dayjs().add(7, "days").format("YYYY-MM-DD");
			if (endDate > lastDayTargetDate) endDate = lastDayTargetDate;

			dispatch(setPlayerFilter({ endDate }));
			if (canAccess("gps")) {
				dispatch(getPlayerGPSData());
			}
		} else {
			dispatch(
				setPlayerFilter({ endDate: dayjs().format(DATE_FORMAT) })
			);
		}
	};

	const handleDateChange = (value, dateString) => {
		dispatch(
			setPlayerFilter({
				startDate: dateString[0] === "" ? null : dateString[0],
				endDate: dateString[1] === "" ? null : dateString[1],
			})
		);
	};

	const handleWeeklyToggle = (checked) => {
		if (checked) {
			dispatch(setPlayerFilter({ showGameDays: false }));
		}
		dispatch(setPlayerFilter({ showWeekly: checked }));
	};

	const handleShowAllDays = (checked) => {
		dispatch(setPlayerFilter({ showAllDays: checked }));
	};

	useEffect(() => {
		if (custom_fp_metrics[teamName]) {
			custom_fp_metrics[teamName].map((val, i) => {
				dispatch(changePlayerFPMetric(i, val));
			});
		}
	}, []);
	const DATE_FORMAT = "YYYY-MM-DD";

	const changeTab = (activeKey) => {
		setActiveTab(activeKey);
	};

	const handleSearch = async () => {
		if (!startDate || !endDate) {
			message.error("Please select valid date range to retrieve training data.");
			return;
		}

		// The helmet tab fetches on its own; a Search click asks it to fetch again.
		if (activeTab === "7") setHelmetSearchCount((count) => count + 1);
		get();
	}

	const get = async () => {
		if (!startDate || !endDate || !player) {
			return;
		}

		// Guarded per endpoint: these views answer 403 for a stream the user cannot
		// access, so the request must not be sent at all.
		const gpsAllowed = canAccess("gps");
		const forceplateAllowed = canAccess("forceplate");

		if (activeTab === "1" || activeTab === "0" ) {
			if (gpsAllowed) dispatch(getPlayerGPSData());
			if (forceplateAllowed) dispatch(getPlayerMovementSignatureData());
		} else if (activeTab === "2") {
			if (gpsAllowed) dispatch(getPlayerGPSData());
		} else if (activeTab === "3") {
			if (forceplateAllowed) dispatch(getPlayerMovementSignatureData());
		} else if (activeTab === "4") {
		}
	};

	useEffect(() => {
		if (location.state !== null && location.state !== undefined) {
			if (location.state.from === "postdata") {
				props.fromPostData(location.state.name, location.state.date);
			} else {
				props.fromACWR(location.state.name);
			}
			location.state = null;
		}
	}, [location, props]);

	useEffect(() => {
		get();
	}, [activeTab, startDate, endDate, player, session, showGameDays, showPredictions, showWeekly]);

	const handleProfileStatChange = () => {
		get();
	}

	useEffect(() => {
		// A retired player leaves team.players but stays in filter.player, so re-pick on a missing id too.
		if (players?.length > 0 && !players.some((p) => p.player_id === player)) {
			dispatch(setPlayerFilter({ player: players[0].player_id }));
		}
	}, [players]);

	useEffect(() => {
		if (teamName === "Nashville Predators") {
			const defaultStartDate = dayjs().subtract(2, "weeks").format(DATE_FORMAT);
			if (startDate === defaultStartDate) {
				dispatch(setPlayerFilter({ startDate: "2025-09-10" }));
			}
		}
	}, [teamName, startDate]);

	const gameDates = useMemo(
		() =>
			trainings?.data?.reduce(
				(cur, data) => ({ ...cur, [data.date]: data.opponent }),
				{}
			),
		[trainings]
	);

	const filteredData = useMemo(
		() =>
			showGameDays && trainings?.data
				? {
						...trainings,
						data: trainings?.data?.filter((data) => {
							return gameDates[data.date]
						}),
						wellness: Object.keys(trainings?.wellness || {}).reduce(
							(cur, key) => ({
								...cur,
								[key]: trainings?.wellness?.[key]?.filter(
									(data) => gameDates[data.date]
								),
							}),
							{}
						),
				  }
				: trainings,
		[trainings, gameDates, showGameDays]
	);
					
	const chartData = useMemo(
		() =>
			filteredData
				? filteredData.data
						?.filter(
							(dt) =>
								dayjs(startDate) <=
									dayjs(dt.date) &&
								dayjs(dt.date) <=
									dayjs(endDate)
						)
				: [],
		[costPerMin, filteredData, practicePlan, trainings]
	);

	const onChangePlayer = async (value) => {
		dispatch(setPlayerFilter({ player: value }));
		dispatch(getPlayerDrillCostPerMin());
	};

	const handleChangeFPMetrics = (key, value) => {
		dispatch(changePlayerFPMetric(key, value));
	};

	const handleSessionChange = (value) => {
		dispatch(setPlayerFilter({ session: value }));
	};

	const onChangeEmailSelection = (option) => {
		setSelectedEmails((prev) => ({
			...prev,
			[option]: !prev[option],
		}));
	};

	const handleComparisonOptionChange = (value) => {
		dispatch(
			setPlayerFilter({ comparisonOption: value })
		);
	};

	const onChangeReportDatatype = (checked) => {
		console.log("checked", checked);
		setSelectedReportDatatype(checked);
		if (checked.length > 0) {
			setBulkGenReportCards(false);
		}
	}

	const handleClickBulkGenReportCards = (e) => {
		const checked = e.target.checked;
		console.log("clicked bulk gen report cards", checked);
		setBulkGenReportCards(checked);
		if (checked) {
			setSelectedReportDatatype([]);
		}
	}

	const popConfirmText = (
		<>
			<Text>
				Would you like to generate a report for a specific type <br />for the current player?
				<br />
				<Checkbox.Group
					style={{ width: '100%' }}
					checked={selectedReportDatatype}
					onChange={onChangeReportDatatype}
					disabled={bulkGenReportCards}
				>
					<Row>
						<Checkbox key="report-card" value="report-card">One-Pager Report Card <Tag color="blue">New</Tag> </Checkbox>
					</Row>
					<Row>
						<Checkbox key="gps" value="gps">GPS</Checkbox>
					</Row>
					<Row>
						<Checkbox key="forceplate" value="forceplate">Forceplate</Checkbox>
					</Row>
					<Row>
						<Checkbox key="testing" value="testing">Testing</Checkbox>
					</Row>
				</Checkbox.Group>
			</Text>

			<div style={{ marginTop: "0.5em" }}>
				<Checkbox
					value={bulkGenReportCards}
					onChange={handleClickBulkGenReportCards}
				>
					Generate one-pager Report Cards for all my players. <br />
					<i>(This may take a few minutes)</i>
				</Checkbox>
			</div>

			<div style={{ marginTop: "0.5em" }}>
				Please select the emails that should receive the report:
			</div>

			<Col style={{ marginTop: "0.5em" }}>
				{coach_list
				.filter((x) => x.email == currentUserEmail)
				.map((item) => (
					<Row>
					<Checkbox
						checked={selectedEmails[item.email] || false}
						onChange={() => onChangeEmailSelection(item.email)}
					>
						{item.first_name} {item.last_name}{" "}
						<span style={{ fontSize: 13 }}>({item.email})</span>
					</Checkbox>
					</Row>
				))}
				{coach_list
				.filter((x) => x.email != currentUserEmail)
				.map((item, index) => (
					<Row key={index}>
					<Checkbox
						checked={selectedEmails[item.email] || false}
						onChange={() => onChangeEmailSelection(item.email)}
					>
						{item.first_name} {item.last_name}{" "}
						<span style={{ fontSize: 13 }}>({item.email})</span>
					</Checkbox>
					</Row>
				))}
			</Col>
		</>
	);

	const handleEmailClick = async () => {
		if (selectedReportDatatype.length <= 0 && !bulkGenReportCards) {
			message.info(
				"Please select at least one data type for the report"
			);
			return;
		}
		let emails = Object.keys(selectedEmails).filter(
			(key) => selectedEmails[key] === true
		);
		if (emails.length === 0) {
			message.info(
				"Please select at least one email recipient for the report"
			);
			return;
		}
		try {
			if (bulkGenReportCards) {
				message.info("Generating report. This may take a few minutes.");
				const res = await trainingAPI.emailBulkReportCards(startDate, endDate, emails);
				if (res === "Done") {
					message.success("The report cards have been sent to your email");
				} else {
					console.log(res);
				}
			} else {
				message.info("Generating report");
				const currPreset = presets.find((preset) => preset.name === profilePresetName)?.config

				const res = await trainingAPI.emailIndividualPlayerReport(player, startDate, endDate, emails, selectedReportDatatype, currPreset || playerProfileStatDefaults[0]);
			if (res === "Done") {
				message.success("The report has been sent to your email");
			} else {}

			}
		} catch (e) {
			if (e === "The report will be sent shortly.") {
				message.info(e);
			} else {
				message.error("Error generating report")
			}
		}
	};

	const handleDownloadPDF = async () => {
		setRendering(true);
		setExportPreview(true);

		try {
			const playerData = players.find(
				({ player_id }) => +player_id === +player
			);
			let descString = `Date Range: ${startDate} - ${endDate}`;
			if (player)
				descString =
					descString +
					` | Player: ${playerData.first_name} ${playerData.last_name}`;
			if (session && activeTab !== "7") descString = descString + ` | Session : ${session}`;

			await new Promise((r) => setTimeout(r, 300));

			const { chartSelector, widgetSelector, withTable = false } = PDF_EXPORT_TARGETS[activeTab] ?? {};
			const chartDOMs = chartSelector ? document.querySelectorAll(chartSelector) : [];
			const widget = widgetSelector ? document.querySelector(widgetSelector) : null;
			const table = withTable ? document.getElementsByTagName("table")[0] : null;

			await renderAsPDF({header: descString, widget, table, columns: 2, chartDOMs});
		} catch (e) {
			message.error("Could not export the PDF. Stay on this tab until the export completes.");
			throw e;
		} finally {
			setExportPreview(false);
			setRendering(false);
		}
	};

	const DaysOptionsContent = () => {
		return (
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					gap: 16,
					width: 140,
				}}
			>
				{activeTab === "1" && (
					<>
						<Switch
							onChange={setShowACWR}
							checked={showACWR}
							checkedChildren="Per Minute"
							unCheckedChildren="ACWR"
						/>
					</>
				)}
				{(activeTab === "1" || activeTab === "2") && (
					<>
						<Switch
							onChange={setShowTravel}
							checked={showTravel}
							checkedChildren="Hide Travel Days"
							unCheckedChildren="Show Travel Days"
						/>
						<Switch
							onChange={setShowRest}
							checked={showRest}
							checkedChildren="Hide Rest Days"
							unCheckedChildren="Show Rest Days"
						/>
					</>
				)}
				{activeTab !== "3" && activeTab !== "4" && (
					<Switch
						onChange={toggleOnlyGameDays}
						checked={showGameDays}
						checkedChildren="Game Days"
						unCheckedChildren="All Days"
					/>
				)}
				{activeTab !== "2" && activeTab !== "3" && activeTab !== "4" && (
					<Switch
						onChange={handleWeeklyToggle}
						checked={showWeekly}
						checkedChildren="Weekly"
						unCheckedChildren="Daily"
					/>
				)}
				{activeTab !== "3" && activeTab !== "4" && (
					<Switch
						onChange={handleShowAllDays}
						checked={showAllDays}
						checkedChildren="All days"
						unCheckedChildren="Days with data"
					/>
				)}
			</div>
		);
	};

	const techStreamExists = (tab) => {
		if (tab === "gps" && datavizLite) return false; // hide GPS for dataviz_lite users
		if (tab === "helmet") return helmetMetrics?.length > 0;
		return allMetricTypes[tab]?.length > 0;
	}

	const hasHelmetData = techStreamExists("helmet");
	const showHelmetTab = sport === "football";

	// Two independent signals: greyed out (disabled) means the team has no such data,
	// the lock icon means the user lacks permission for it. A tab with data but no
	// permission stays clickable and explains itself when opened.
	const customTabPane = ({ key, label, locked = !canAccess(key) }) => {
		return (
			<span
				style={{ display: 'flex', alignItems: 'center'}}
			>
				{label} {locked && <TfiLock style={{ marginLeft: 2}}/>}
			</span>
		)
	}

	const getFirstTabToDisplay = () => {
		// const tabs = ['gps', 'wellness', 'forceplate', 'testing', 'nutrition'];
		// for (let i = 0; i < tabs.length; i++) {
		// 	if (techStreamExists(tabs[i])) {
		// 		return {
		// 			"gps": "1",
		// 			"wellness": "2",
		// 			"forceplate": "3",
		// 			"testing": "4",
		// 			"nutrition": "5"
		// 		}[tabs[i]];
		// 	}
		// }
		// Always show profile tab first on player page
		return "0"
	}

	useEffect(() => {
		// this is needed to update the activeTab state variable once all data is loaded
		setActiveTab(getFirstTabToDisplay());
	}, []);

	useEffect(() => {
		if (trainings?.colors && trainings?.metrics?.length && colorStrategies?.length) {
			setDataColorUtil(new DataColorUtil({
				teamName,
				gpsBaselines: trainings.colors,
				teamGpsMetrics: trainings.metrics,
				colorStrategies
			}));
		} else {
			setDataColorUtil(null);
		}
	}, [trainings?.colors, trainings?.metrics, teamName, colorStrategies]);

	return (
		<>
			<Navbar />
			<HelpButton page="Player"/>
			<form id="request">
				<FloatButton.BackTop style={{ insetInlineEnd: "auto", insetInlineStart: 24 }} />
				<div className="search-divider"></div>
				<div
					style={{
						textAlign: "center",
					}}
				>
					<Space>
						<Select
							showSearch
							// The Notes tab shows only this picker in the bar, so it is 30% wider there.
							style={{ width: activeTab === "notes" ? 234 : 180 }}
							placeholder="Choose Player"
							value={player}
							onChange={onChangePlayer}
							filterOption={(input, option) => {
								return option.label
									.toLowerCase()
									.indexOf(input.toLowerCase()) >= 0
							}}
						>
							{players !== null ? (
								players.map((player, i) => {
									return PlayerOptions({ player, teamName, index: i, playersStatus });
								})
							) : (
								<></>
							)}
						</Select>
						{activeTab !== "notes" && (
							<>
								<RangePicker
									onChange={handleDateChange}
									value={[
										startDate !== null
											? dayjs(startDate)
											: null,
										endDate !== null
											? dayjs(endDate)
											: null,
									]}
									style={{ width: 250 }}
									allowClear={false}
								/>
								{/* The helmet tab reads no session, day option or prediction, so tab 7 hides them, as the Team page does. */}
								{activeTab !== "7" && (
									<Select
										allowClear
										placeholder="All Sessions"
										value={session}
										style={{ width: 180 }}
										onChange={handleSessionChange}
									>
										{session_list.map((session) => {
											return (
												<Option value={session}>{session}</Option>
											);
										})}
									</Select>
								)}
								{activeTab !== "7" && (
									<Popover
										content={<DaysOptionsContent />}
										title="Day Options"
										trigger="click"
									>
										<Button>Options</Button>
									</Popover>
								)}
								{activeTab !== "2" && activeTab !== "3" && activeTab !== "4" && activeTab !== "7" && (
									<Switch
										onChange={handleTogglePredictions}
										checked={showPredictions}
										checkedChildren="Hide Predictions"
										unCheckedChildren="Show Predictions"
									/>
								)}

								{player ? (
									<>
										<PermissionedButton
											type="primary"
											onClick={handleSearch}
											permissionCheck={activeStreamPermission}
										>
											Search
										</PermissionedButton>
										{activeTab === "0" && (
											<Popconfirm
												placement="bottom"
												title={popConfirmText}
												icon={<FileTwoTone />}
												onConfirm={handleEmailClick}
												okText="Yes"
												cancelText="No"
											>
												<PermissionedButton permissionCheck={activeStreamPermission}>Get Report</PermissionedButton>
											</Popconfirm>
										)}
										<PermissionedButton
											type="primary"
											onClick={() => {
												handleDownloadPDF();
											}}
											loading={rendering}
											permissionCheck={activeStreamPermission}
										>
											Export as PDF
										</PermissionedButton>
									</>
								) : (
									<>
										<Tooltip title="No player selected">
											<Button type="primary" disabled>
												Search
											</Button>
										</Tooltip>
										<Tooltip title="No player selected">
											<Button disabled>Export as PDF</Button>
										</Tooltip>
									</>
								)}
							</>
						)}
					</Space>
				</div>

				<div className="data-divider"></div>
				{/* The injury timeline does not apply to the notes tab. */}
				{activeTab !== "notes" &&
				trainings?.length !== 0 &&
				trainings["injuries"]?.length !== 0 ? (
					<div className="timeline-collapse">
						<Collapse className="">
							<Panel header="Show Injury Timeline">
								<div className="timeline-timeline">
									<Timeline mode={"left"}>
										{trainings["injuries"]?.map(
											(injury, i) => {
												return (
													<Timeline.Item
														color={injury["color"]}
														label={injury["date"]}
													>
														{injury["text"]}
													</Timeline.Item>
												);
											}
										)}
									</Timeline>
								</div>
							</Panel>
						</Collapse>
					</div>
				) : (
					<></>
				)}
			</form>
			<Tabs
				activeKey={activeTab}
				type="card"
				onChange={changeTab}
				centered
			>
				<TabPane
					tab="Profile"
					key="0"
					style={{
						width: "85%",
						margin: "0 auto",
						display: "flex",
						justifyContent: "center",
						flexDirection: "column",
					}}
				>
					{loading ? (
						<Spin size="large" />
					) : trainings["data"] !== undefined && activeTab === "0" ? (
						<LoadingComponent
							loading={rendering}
							title="Rendering to PDF file..."
						>
							<Profile
								showACWR={showACWR}
								showTravel={showTravel}
								showRest={showRest}
								handleProfileStatChange={handleProfileStatChange}
								onOpenNotes={canViewNotes ? () => setActiveTab("notes") : undefined}
							/>
						</LoadingComponent>
					) : (
						<></>
					)}
				</TabPane>
				<TabPane
					tab={customTabPane({key: "gps", label: "GPS"})}
					key="1"
					disabled={!techStreamExists("gps")}
				>
					<DataAccessGuard stream="gps" variant="inline">
					{loading ? (
						<Spin size="large" />
					) : trainings["data"] !== undefined && activeTab === "1" ? (
						<LoadingComponent
							loading={rendering}
							title="Rendering to PDF file..."
						>
							{trainings["data"]?.length !== 0 ? (
								<PlayerCharts
									showACWR={showACWR}
									showTravel={showTravel}
									showRest={showRest}
									dataIn={{
										...filteredData,
										data: chartData,
									}}
									hideDistanceZones={hideDistanceZones}
									showAll={exportPreview}
									type="player"
									dataColorUtil={dataColorUtil}
									handleComparisonOptionChange={handleComparisonOptionChange}
								/>
							) : (
								<p>No data for your query.</p>
							)}
						</LoadingComponent>
					) : (
						<></>
					)}
					</DataAccessGuard>
				</TabPane>
				<TabPane
					tab={customTabPane({key: "wellness", label: "Wellness"})}
					key="2"
					disabled={!techStreamExists("wellness")}
				>
					<DataAccessGuard stream="wellness" variant="inline">
						{loading ? (
							<Spin size="large" />
						) : Math.max(
								...Object.keys(trainings?.wellness || {}).map(
									(k) => trainings?.wellness[k]?.length
								)
							) > 0 && activeTab === "2" ? (
							<LoadingComponent
								loading={rendering}
								title="Rendering to PDF file..."
							>
								<Subjective
									data={filteredData}
									loading={loading}
									allGames={games}
									playersPage={true}
									showAll={exportPreview}
									showRestTravelOptions={{
										showTravel,
										showRest,
										startDate,
										endDate,
									}}
									type="player"
									handleComparisonOptionChange={handleComparisonOptionChange}
								/>
							</LoadingComponent>
						) : (
							<p>No data for your query.</p>
						)}
					</DataAccessGuard>
				</TabPane>
				<TabPane
					tab={customTabPane({key: "forceplate", label: "Force Plate"})}
					key="3"
					disabled={!techStreamExists("forceplate")}
				>
					<DataAccessGuard stream="forceplate" variant="inline">
						{props.loading ? (
							<Spin size="large" />
						) : activeTab === "3" ? (
							<LoadingComponent
								loading={rendering}
								title="Rendering to PDF file..."
							>
								<ForcePlate
									data={{
										...fpmsData,
										data: fpmsData?.data?.sort((a, b) =>
											a.date < b.date ? -1 : 1
										),
									}}
									movement_sig_chart_mapping={movement_sig_chart_mapping}
									metrics={fpMetrics}
									fp_change_metric={handleChangeFPMetrics}
									team_name={teamName}
									type="player"
									dataColorUtil={dataColorUtil}
									handleComparisonOptionChange={handleComparisonOptionChange}
								/>
							</LoadingComponent>
						) : (
							<></>
						)}
					</DataAccessGuard>
				</TabPane>
				<TabPane
					tab={customTabPane({key: "testing", label: "Testing"})}
					key="4"
					disabled={!techStreamExists("testing")}
				>
					<DataAccessGuard stream="testing" variant="inline">
						{props.loading ? (
							<Spin size="large" />
						) : activeTab === "4" ? (
							<LoadingComponent
								loading={rendering}
								title="Rendering to PDF file..."
							>
								<TestingData
									showAll={exportPreview}
									type="player"
									dataColorUtil={dataColorUtil}
									handleComparisonOptionChange={handleComparisonOptionChange}
								/>
							</LoadingComponent>
						) : (
							<></>
						)}
					</DataAccessGuard>
				</TabPane>
				<TabPane
					tab={customTabPane({key: "nutrition", label: "Nutrition"})}
					key="5"
					id="nutrition"
					disabled={!techStreamExists("nutrition")}
				>
					<DataAccessGuard stream="nutrition" variant="inline">
						{props.loading ? (
						<Spin size="large" />
					) : activeTab === "5" ? (
						<LoadingComponent
							loading={rendering}
							title="Rendering to PDF file..."
						>
							<Nutrition
								showAll={exportPreview}
								type="player"
							/>
						</LoadingComponent>
					) : (
						<></>
					)}
					</DataAccessGuard>
				</TabPane>
				<TabPane
					tab={customTabPane({key: "cognition", label: "Cognition"})}
					key="6"
					id="cognition"
					disabled={!techStreamExists("cognition")}
				>
					<DataAccessGuard stream="cognition" variant="inline">
					{props.loading ? (
					<Spin size="large" />
				) : activeTab === "6" ? (
					<LoadingComponent
						loading={rendering}
						title="Rendering to PDF file..."
					>
						<Cognition
							showAll={exportPreview}
							type="player"
							dataColorUtil={dataColorUtil}
							handleComparisonOptionChange={handleComparisonOptionChange}
						/>
					</LoadingComponent>
				) : (
					<></>
				)}
					</DataAccessGuard>
				</TabPane>
				{/* Football only. Without a helmet integration the tab is disabled and its popover sells the feature, as on the Team page. */}
				{showHelmetTab ? (
					<TabPane
						tab={
							hasHelmetData ? (
								customTabPane({ key: "helmet", label: "Helmet Impact" })
							) : (
								<Popover content={<HelmetImpactPitch teamName={teamName} />} overlayClassName="helmet-pitch-popover">
									{customTabPane({ key: "helmet", label: "Helmet Impact", locked: false })}
								</Popover>
							)
						}
						key="7"
						id="helmet"
						disabled={!hasHelmetData}
					>
						{activeTab === "7" && hasHelmetData && player && (
							<DataAccessGuard stream="helmet" variant="inline">
								{/* Spin with `spinning` keeps the tab mounted, so the PDF keeps the selected day and the filters. */}
								<Spin spinning={rendering} tip="Rendering to PDF file..." size="large">
									<HelmetImpact showAll={exportPreview} searchCount={helmetSearchCount} playerId={Number(player)} />
								</Spin>
							</DataAccessGuard>
						)}
					</TabPane>
				) : null}
				{/* The same frame as the Profile tab, so the notes line up with the player card. */}
				<TabPane
					tab={customTabPane({ key: "notes", label: "Notes", locked: !canViewNotes })}
					key="notes"
					className="player-notes-pane"
					style={{ width: "85%", margin: "0 auto" }}
				>
					{activeTab === "notes" && player && <NotesTab fixedPlayerId={Number(player)} />}
				</TabPane>
			</Tabs>
		</>
	);
}

export default PlayerData;
